import { useState, useEffect } from "react";
import axios from "axios";
import { Link, useNavigate } from "react-router-dom";
import API_BASE_URL from 'config/api';

// @mui material components
import Checkbox from "@mui/material/Checkbox";

// Material Dashboard 3 PRO React components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDInput from "components/MDInput";
import MDButton from "components/MDButton";

// Authentication layout components
import IllustrationLayout from "layouts/authentication/components/IllustrationLayout";

// Image
import logoImage from "assets/images/illustrations/logicookie-white.png";
import { extractRoleFromToken, isAdminRole } from "utils/auth";

function Illustration() {
  const [rememberMe, setRememberMe] = useState(false);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const navigate = useNavigate();

  // Sayfa yüklendiğinde sadece email/password'u localStorage'dan çek
  useEffect(() => {
    const savedEmail = window.localStorage.getItem("rememberedEmail");
    const savedPassword = window.localStorage.getItem("rememberedPassword");
    if (savedEmail && savedPassword) {
      setEmail(savedEmail);
      setPassword(savedPassword);
      setRememberMe(true);
    }
  }, []);

  const handleSetRememberMe = () => {
    const newRememberMe = !rememberMe;
    setRememberMe(newRememberMe);

    if (!newRememberMe) {
      window.localStorage.removeItem("rememberedEmail");
      window.localStorage.removeItem("rememberedPassword");
    }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");

    try {
      const response = await axios.post(
        `${API_BASE_URL}/auth/login`,
        { email, password },
        { headers: { "Content-Type": "application/json" } }
      );

      if (response.data.status === 200) {
        const { token, userId } = response.data;
        const role = extractRoleFromToken(token);

        if (!token || !isAdminRole(role)) {
          window.sessionStorage.removeItem("authToken");
          window.sessionStorage.removeItem("userData");
          window.sessionStorage.removeItem("userRole");
          setError("Bu panele yalnızca admin veya superadmin rollerindeki kullanıcılar giriş yapabilir.");
          return;
        }

        if (rememberMe) {
          window.localStorage.setItem("rememberedEmail", email);
          window.localStorage.setItem("rememberedPassword", password);
        } else {
          window.localStorage.removeItem("rememberedEmail");
          window.localStorage.removeItem("rememberedPassword");
        }

        window.sessionStorage.setItem("authToken", token);
        window.sessionStorage.setItem(
          "userData",
          JSON.stringify({
            userId,
            email,
            role,
          })
        );
        window.sessionStorage.setItem("userRole", role);

        navigate("/dashboard");
      } else {
        setError(
          response.data.message ||
            "Giriş başarısız. Lütfen bilgilerinizi kontrol edin."
        );
      }
    } catch (err) {
      setError(
        err.response?.data?.message || "Bir hata oluştu. Lütfen tekrar deneyin."
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <IllustrationLayout
      title="Giriş Yap"
      description="E-posta ve şifrenizi girerek giriş yapın"
      illustration={logoImage}
    >
      <MDBox component="form" role="form" onSubmit={handleLogin}>
        {error && (
          <MDBox mb={2}>
            <MDTypography variant="body2" color="error">
              {error}
            </MDTypography>
          </MDBox>
        )}
        <MDBox mb={2}>
          <MDInput
            type="email"
            label="E-posta"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            fullWidth
            required
            sx={{
              "& .MuiInputLabel-root": {
                lineHeight: "1.2",
                top: "-2px",
              },
            }}
          />
        </MDBox>
        <MDBox mb={2}>
          <MDInput
            type="password"
            label="Şifre"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            autocomplete="current-password"
            fullWidth
            required
            sx={{
              "& .MuiInputLabel-root": {
                lineHeight: "1.2",
                top: "-2px",
              },
            }}
          />
        </MDBox>
        <MDBox display="flex" alignItems="center" ml={-1}>
          <Checkbox
            checked={rememberMe}
            onChange={handleSetRememberMe}
            color="primary"
          />
          <MDTypography
            variant="button"
            fontWeight="regular"
            color="text"
            onClick={handleSetRememberMe}
            sx={{ cursor: "pointer", userSelect: "none", ml: -1 }}
          >
            &nbsp;&nbsp;Beni hatırla
          </MDTypography>
        </MDBox>
        <MDBox mt={4} mb={1}>
          <MDButton
            variant="gradient"
            color="dark"
            size="large"
            fullWidth
            type="submit"
            disabled={loading}
          >
            {loading ? "Giriş Yapılıyor..." : "Giriş Yap"}
          </MDButton>
        </MDBox>
        <MDBox mt={3} textAlign="center">
          <MDTypography
            component={Link}
            to="/reset-password"
            variant="button"
            color="info"
            fontWeight="medium"
            textGradient
          >
            Şifrenizi mi unuttunuz? Yeni Şifre İçin Tıklayın!
          </MDTypography>
        </MDBox>
      </MDBox>
    </IllustrationLayout>
  );
}

export default Illustration;
